import { Button } from '@shared/components/ui/button';
import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

import {
  faXmark,
  faBoxOpen,
  faXmarkCircle,
  faCircleCheck,
  faTimes,
  faChevronDown,
} from '@fortawesome/pro-light-svg-icons';
import { memo, useMemo, useCallback, useRef, useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';

interface SelectionToolbarProps {
  selectedCount: number;
  onClose: () => void;
  onCancelSelected: () => void;
  onPrepareSelected: () => void;
  onMarkForSendSelected: () => void;
  onUncancelSelected: () => void;
  // Contadores para cada acción (opcional, para mostrar cuántos elementos son elegibles para cada acción)
  cancelableCount?: number;
  preparableCount?: number;
  sendableCount?: number;
  uncancelableCount?: number;
}

// Componente memoizado para los botones de acción
const ActionButton = memo(
  ({
    icon,
    label,
    count,
    onClick,
    disabled,
    iconColor,
  }: {
    icon: any;
    label: string;
    count: number;
    onClick: () => void;
    disabled: boolean;
    iconColor: string;
  }) => (
    <Button
      variant="outline"
      size="sm"
      onClick={onClick}
      className={`border-card-border bg-transparent ${disabled ? 'opacity-50' : ''}`}
      disabled={disabled}
    >
      <FontAwesomeIcon icon={icon} className={`w-3 h-3 mr-1 ${iconColor}`} />
      {label} ({count})
    </Button>
  ),
);

ActionButton.displayName = 'ActionButton';

export const SelectionToolbar = memo(
  ({
    selectedCount,
    onClose,
    onCancelSelected,
    onPrepareSelected,
    onMarkForSendSelected,
    onUncancelSelected,
    cancelableCount = 0,
    preparableCount = 0,
    sendableCount = 0,
    uncancelableCount = 0,
  }: SelectionToolbarProps) => {
    const { t } = useTranslation('ordersShippedModule');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const isMobile = typeof window !== 'undefined' ? window.innerWidth < 768 : false;

    // Referencias a las modales de confirmación
    const cancelModalRef = useRef<ConfirmModalRef>(null);
    const markForSendModalRef = useRef<ConfirmModalRef>(null);
    const uncancelModalRef = useRef<ConfirmModalRef>(null);

    // Iconos para las acciones de selección
    const selectionActionIcons = {
      cancel: faXmarkCircle,
      prepare: faBoxOpen,
      markForSend: faCircleCheck,
      uncancel: faXmark,
    };

    // Memoizar las traducciones para evitar recalcularlas
    const translations = useMemo(
      () => ({
        selected: t('selection.selected'),
        cancel: t('selection.cancel'),
        prepareOrders: t('selection.prepareOrders'),
        markForSend: t('selection.markForSend'),
        uncancel: t('selection.uncancel'),
        actions: t('selection.actions', 'Acciones'),
      }),
      [t],
    );

    // Memoizar los handlers para evitar recrearlos en cada render
    const handleCancel = useCallback(() => {
      if (cancelableCount > 0) {
        cancelModalRef.current?.open(
          () => onCancelSelected(),
          undefined,
          t('confirmModals.bulkCancel.title'),
          t('confirmModals.bulkCancel.message', { count: cancelableCount }),
          t('buttons.confirm'),
          t('buttons.cancel'),
        );
      }
    }, [onCancelSelected, cancelableCount, t]);

    const handlePrepare = useCallback(() => {
      onPrepareSelected();
    }, [onPrepareSelected]);

    const handleMarkForSend = useCallback(() => {
      if (sendableCount > 0) {
        markForSendModalRef.current?.open(
          () => onMarkForSendSelected(),
          undefined,
          t('confirmModals.bulkMarkForSend.title'),
          t('confirmModals.bulkMarkForSend.message', { count: sendableCount }),
          t('buttons.confirm'),
          t('buttons.cancel'),
        );
      }
    }, [onMarkForSendSelected, sendableCount, t]);

    const handleUncancel = useCallback(() => {
      if (uncancelableCount > 0) {
        uncancelModalRef.current?.open(
          () => onUncancelSelected(),
          undefined,
          t('confirmModals.bulkUncancel.title'),
          t('confirmModals.bulkUncancel.message', { count: uncancelableCount }),
          t('buttons.confirm'),
          t('buttons.cancel'),
        );
      }
    }, [onUncancelSelected, uncancelableCount, t]);

    const handleClose = useCallback(() => {
      onClose();
    }, [onClose]);

    // Si no hay elementos seleccionados, no renderizar nada
    if (selectedCount === 0) {
      return null;
    }

    return (
      <>
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-card-bg border border-card-border rounded-lg p-2 shadow-lg">
            <div className="flex items-center gap-4">
              {/* Contador de seleccionados */}
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 bg-primary text-white text-sm rounded-full flex items-center justify-center">
                  {selectedCount}
                </span>
                <span className="text-sm font-medium">{translations.selected}</span>
              </div>

              {/* Versión Desktop: Botones horizontales */}
              {!isMobile && (
                <div className="flex items-center gap-1">
                  <ActionButton
                    icon={selectionActionIcons.cancel}
                    label={translations.cancel}
                    count={cancelableCount}
                    onClick={handleCancel}
                    disabled={cancelableCount === 0}
                    iconColor="text-error"
                  />

                  <ActionButton
                    icon={selectionActionIcons.prepare}
                    label={translations.prepareOrders}
                    count={preparableCount}
                    onClick={handlePrepare}
                    disabled={preparableCount === 0}
                    iconColor="text-primary"
                  />

                  <ActionButton
                    icon={selectionActionIcons.markForSend}
                    label={translations.markForSend}
                    count={sendableCount}
                    onClick={handleMarkForSend}
                    disabled={sendableCount === 0}
                    iconColor="text-success"
                  />

                  <ActionButton
                    icon={selectionActionIcons.uncancel}
                    label={translations.uncancel}
                    count={uncancelableCount}
                    onClick={handleUncancel}
                    disabled={uncancelableCount === 0}
                    iconColor=""
                  />
                </div>
              )}

              {/* Versión Mobile: Dropdown de acciones */}
              {isMobile && (
                <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="border-card-border bg-transparent">
                      {translations.actions}
                      <FontAwesomeIcon icon={faChevronDown} className="ml-2 w-3 h-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52 border-card-border">
                    <DropdownMenuItem
                      onClick={handleCancel}
                      disabled={cancelableCount === 0}
                      className={cancelableCount === 0 ? 'opacity-50' : ''}
                    >
                      <FontAwesomeIcon icon={selectionActionIcons.cancel} className="mr-2 w-3.5 text-error" />
                      {translations.cancel} ({cancelableCount})
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={handlePrepare}
                      disabled={preparableCount === 0}
                      className={preparableCount === 0 ? 'opacity-50' : ''}
                    >
                      <FontAwesomeIcon icon={selectionActionIcons.prepare} className="mr-2 w-3.5 text-primary" />
                      {translations.prepareOrders} ({preparableCount})
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={handleMarkForSend}
                      disabled={sendableCount === 0}
                      className={sendableCount === 0 ? 'opacity-50' : ''}
                    >
                      <FontAwesomeIcon icon={selectionActionIcons.markForSend} className="mr-2 w-3.5 text-success" />
                      {translations.markForSend} ({sendableCount})
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={handleUncancel}
                      disabled={uncancelableCount === 0}
                      className={uncancelableCount === 0 ? 'opacity-50' : ''}
                    >
                      <FontAwesomeIcon icon={selectionActionIcons.uncancel} className="mr-2 w-3.5" />
                      {translations.uncancel} ({uncancelableCount})
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* Botón cerrar */}
              <Button variant="ghost" size="sm" onClick={handleClose} className="p-0 hover:bg-transparent">
                <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Modales de confirmación */}
        <ConfirmModal ref={cancelModalRef} />
        <ConfirmModal ref={markForSendModalRef} />
        <ConfirmModal ref={uncancelModalRef} />
      </>
    );
  },
);

SelectionToolbar.displayName = 'SelectionToolbar';
